import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getThirdPartyOauth, getUserConnections } from 'services/userServices'
import { Button, Dialog } from '@mui/material'
import Grid from '@mui/material/Grid'
import OverlayLoading from 'components/atoms/OverlayLoading'
import InCardStack from 'components/atoms/container/InCardStack'
import cursorHighlightClickIcon from 'assets/images/cursorHighlightClick.svg'
import RowStack from 'components/atoms/container/RowStack'
import { getRequest, postRequest } from 'utils/api'
import {
  USER_CONNECTION_EVENTS_IMPORT_URL,
  USER_CONNECTION_EVENT_TYPES,
  USER_CONNECTION_RELATIONSHIP
} from 'services/constants'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import ImportMeetingItem from 'components/molecules/myTeam/ImportMeetingItem'
import {
  CalendarLinkedStep,
  DialogBodyContainer,
  FinishImportStep,
  LinkCalendarStep,
  NoMeetingFoundStep,
  SkipForNowButton
} from 'components/molecules/myTeam/ImportOneOnOneMolecules'
import { trackEvent } from 'services/amplitudeTracking'
import moment from 'moment'
import ImportOneOnOneUpdateConn from 'components/organisms/myTeam/ImportOneOnOneUpdateConn'
import AddNoteTakerBody from 'components/molecules/myTeam/AddNoteTakerBody'
import { updatePageState } from 'utils/windowHistoryHelper'

const ImportOneOnOne = ({ sucessCallback, buttonProps }) => {
  const { t } = useTranslation()

  const [selected, setSelected] = useState([])
  const [candidates, setCandidates] = useState([])
  const [currentStep, setCurrentStep] = useState(2)
  const [stepCount, setStepCount] = useState(2)
  const [showConnUpdatePopup, setShowConnUpdatePopup] = useState(false)
  const [importedEvent, setImportedEvent] = useState(null)
  const [importedUserConnEvents, setImportedUserConnEvents] = useState(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const maxStepCount =  4 

  useEffect(() => {
    if (window.history?.state?.usr?.openImportOneOnOne) {
      setOpen(true)
      updatePageState({ usr: { openImportOneOnOne: false } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.history?.state])

  const setCandidatesWithRelations = async (
    candidateData = candidates,
    selectedCandidates = selected,
    submitImport = false
  ) => {
    setLoading(true)
    const connections = await getUserConnections()
    const candidateWithRelationship = candidateData.map(c => {
      const connection = connections.find(
        x =>
          (x.userId && x.userId === c.attendeeUser?.id) ||
          x.email === c.attendee?.email
      )
      if (!connection) {
        return c
      }

      return {
        ...c,
        attendeeUser: {
          ...(c.attendeeUser || connection.user),
          userConnectionId: connection?.id,
          relationship: connection?.relationship
        }
      }
    })
    setCandidates(candidateWithRelationship)
    if (selectedCandidates.length > 0) {
      const newSelected = selectedCandidates.map(s =>
        candidateWithRelationship.find(
          c => c.masterCalendarEventId === s.masterCalendarEventId
        )
      )
      setSelected(newSelected)
      if (submitImport) {
        await handleNextStep(newSelected)
      }
    }
    setLoading(false)
  }

  const fetchImport = async () => {
    setLoading(true)
    setSelected([])
    const res = await getRequest(USER_CONNECTION_EVENTS_IMPORT_URL)
    if (res?.status === 200) {
      setCandidatesWithRelations(res.data.data, [])
    } else {
      setCandidates([])
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchLinked = async () => {
      setLoading(true)
      const res = await getThirdPartyOauth()
      if (res?.status === 200) {
        setCurrentStep(2)
        setStepCount(maxStepCount - 1)
        await fetchImport() // Loading removed in fetchImport
      } else {
        setCurrentStep(0)
        setStepCount(maxStepCount)
        setLoading(false)
      }
    }
    if (open) {
      fetchLinked()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const importOneOnOne = async (newSelected = selected) => {
    setLoading(true)
    const requestBody = {
      events: newSelected.map(c => ({
        masterCalendarEventId: c.masterCalendarEventId,
        attendee: c.attendee
      })),
      timeZone: moment.tz.guess(),
      type: USER_CONNECTION_EVENT_TYPES.ONE_TO_ONE
    }
    const res = await postRequest(
      USER_CONNECTION_EVENTS_IMPORT_URL,
      requestBody
    )
    if (res?.status === 201) {
      const imported = res.data.data
      const importedCandidates = imported.map(x => {
        const importedCandidate = newSelected.find(
          i => i.masterCalendarEventId === x.masterCalendarEventId
        )
        importedCandidate.id = x.id
        importedCandidate.userConnectionEventId = x.id
        importedCandidate.meetingUrl = x.meetingUrl
        importedCandidate.userConnectionEvent = x
        return importedCandidate
      })
      trackEvent('Meeting Imported')
      setImportedEvent(importedCandidates[0])
      setImportedUserConnEvents(importedCandidates)
      setLoading(false)
    } // TODO: handle error
    setLoading(false)
  }
  const handleNextStep = async (newSelected = selected) => {
    if (currentStep === 1) {
      await fetchImport()
    }
    if (currentStep === 2) {
      // Check if any of the selected candidates have a attendeeUser have relationship then it should not direct_report
      if (
        newSelected.some(
          x =>
            x.attendeeUser &&
            x.attendeeUser?.relationship &&
            x.attendeeUser?.relationship !==
              USER_CONNECTION_RELATIONSHIP[0].value
        )
      ) {
        setShowConnUpdatePopup(true)
        return
      } else {
        await importOneOnOne(newSelected)
      }
    }
    if (currentStep < maxStepCount) {
      setCurrentStep(currentStep + 1)
    } else {
      setOpen(false)
      if (importedEvent) {
        sucessCallback()
      }
    }
  }

  const notStepSetup =
    currentStep === 0 ||
    (currentStep === 2 && candidates.length === 0 && !loading)
  const currentProgress = stepCount - (maxStepCount - currentStep)
  return (
    <>
      <Button
        onClick={() => {
          trackEvent('Click import meeting')
          setOpen(true)
        }}
        {...buttonProps}>
        {t('calendarEvents.import1on1')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <OverlayLoading open={loading} />

        <Grid
          container
          padding={12}
          sx={{
            width: 568,
            height: notStepSetup ? 'auto' : 700,
            paddingTop: 20
          }}>
          {currentStep === 0 ? (
            <LinkCalendarStep
              handleSigninSuccess={() => setCurrentStep(1)}
              onExit={() => setOpen(false)}
            />
          ) : currentStep === 1 ? (
            <CalendarLinkedStep
              currentProgress={currentProgress}
              stepCount={stepCount}
            />
          ) : currentStep === 2 ? (
            !loading && (
              <>
                {candidates.length > 0 ? (
                  <DialogBodyContainer
                    icon={cursorHighlightClickIcon}
                    title={t('calendarEvents.select1on1')}
                    description={t('calendarEvents.identified1on1')}
                    currentProgress={currentProgress}
                    stepCount={stepCount}>
                    <BoxWithBackground width='100%' sx={{ padding: 8 }}>
                      <InCardStack
                        spacing={4}
                        width='100%'
                        maxHeight={230}
                        overflow='auto'>
                        {candidates.map(x => (
                          <ImportMeetingItem
                            key={x.masterCalendarEventId}
                            candidate={x}
                            selected={selected}
                            setSelected={setSelected}
                          />
                        ))}
                      </InCardStack>
                    </BoxWithBackground>
                  </DialogBodyContainer>
                ) : (
                  <NoMeetingFoundStep onClose={() => setOpen(false)} />
                )}
              </>
            )
          ) : currentStep === 3 ? (
            <AddNoteTakerBody
              candidates={importedUserConnEvents}
              setLoading={setLoading}
              onSuccess={() => handleNextStep()}
              cancelBtnText='calendarEvents.skipNotetaker'
              cancelBtnAction={() => handleNextStep()}
              progressBar={true}
              currentProgress={currentProgress}
              stepCount={stepCount}
              showSkip={true}
            />
          ) : (
            <FinishImportStep
              importedEvent={importedEvent}
              stepCount={stepCount}
              onExit={() => setOpen(false)}
            />
          )}
          {!notStepSetup && (
            <>
              {currentStep === 3 ? null : (
                <Grid size={12} alignSelf='flex-end' height={44}>
                  <RowStack justifyContent='center'>
                    {currentStep !== maxStepCount && (
                      <SkipForNowButton onExit={() => setOpen(false)} />
                    )}
                    <Button
                      disabled={currentStep === 2 && !selected.length}
                      onClick={() => handleNextStep()}>
                      {currentStep === 1
                        ? t('calendarEvents.findExisting1on1')
                        : currentStep === 2
                          ? t('calendarEvents.importSelected', {
                              count: selected.length
                            })
                          : t('common.finish')}
                    </Button>
                  </RowStack>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Dialog>

      {showConnUpdatePopup && (
        <ImportOneOnOneUpdateConn
          open={showConnUpdatePopup}
          setOpen={setShowConnUpdatePopup}
          inDirectCandidates={selected
            .filter(
              x =>
                x.attendeeUser &&
                x.attendeeUser?.relationship &&
                x.attendeeUser?.relationship !==
                  USER_CONNECTION_RELATIONSHIP[0].value
            )
            .map(x => x.attendeeUser)}
          sucessCallback={() =>
            setCandidatesWithRelations(candidates, selected, true)
          }
          closeImport={() => setOpen(false)}
        />
      )}
    </>
  )
}

export default ImportOneOnOne
